import type { AdaptedProduct } from '@/types'
import type { Context } from '@/types'
import { matchKey } from '@/utils/matchKey.utils'

export type DimensionRangeItem = {
  key?: string | null
  min?: number | null
  max?: number | null
}

export type SavedSearchItem = {
  id: string
  byUserID?: string | null
  projectID?: string | null
  slug?: string | null
  organizationID?: string | null
  organizationMasterID?: string | null
  location?: { lat?: number; lon?: number } | null
  name?: string | null
  source?: string | null
  status?: string | null
  description?: string | null
  conditions?: string[] | null
  provenances?: string[] | null
  categories?: string[] | null
  dimensionRange?: DimensionRangeItem[] | null
  quantity?: { number?: number; unit?: string } | null
  brands?: string[] | null
  models?: string[] | null
  materials?: string[] | null
  address?: string | null
  startDate?: string | null
  endDate?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

const resolveIdToLabelValue = (
  id: string | null | undefined,
  options: Parameters<typeof matchKey>[1] | undefined,
): { label: string; value: string } | undefined => {
  if (id == null || id === '') return undefined
  if (!options?.length) return { label: id, value: id }
  const match = matchKey(id, options)
  return { label: match?.label ?? id, value: id }
}

/**
 * Adapt SavedSearch to AdaptedProduct format for alert UI compatibility.
 * useAlertSearch and alert components expect AdaptedProduct shape.
 * Pass context to resolve projectID, organizationID, byUserID to labels for grid display.
 */
export const savedSearchToAdaptedProduct = (
  item: SavedSearchItem,
  context?: Partial<Context>,
): AdaptedProduct => {
  const provenances = (item.provenances ?? []).map((p) => ({
    label: p,
    value: p,
  }))
  const material = (item.materials ?? []).map((m) => ({
    label: m,
    value: m,
  }))
  const conditions = (item.conditions ?? []).map((c) => ({
    label: c,
    value: c,
  }))

  return {
    id: item.id,
    productID: item.id,
    name: item.name
      ? { label: item.name, value: item.name }
      : { label: '', value: '' },
    provenances: provenances.length ? provenances : undefined,
    conditions: conditions.length ? conditions : undefined,
    material: material.length ? material : undefined,
    'quantity.number':
      item.quantity?.number != null
        ? { value: item.quantity.number, label: String(item.quantity.number) }
        : undefined,
    'quantity.unit': item.quantity?.unit
      ? { value: item.quantity.unit, label: item.quantity.unit }
      : undefined,
    startDate: item.startDate
      ? { value: item.startDate, label: item.startDate }
      : undefined,
    endDate: item.endDate
      ? { value: item.endDate, label: item.endDate }
      : undefined,
    location:
      item.location?.lat != null && item.location?.lon != null
        ? {
            lat: item.location.lat,
            lon: item.location.lon,
            value: `${item.location.lat},${item.location.lon}`,
            label: `${item.location.lat},${item.location.lon}`,
          }
        : undefined,
    spot: item.address
      ? {
          label: item.address ?? '',
          value: item.address ?? '',
        }
      : undefined,
    internalNotes: item.description
      ? { label: item.description, value: item.description }
      : undefined,
    projectID: resolveIdToLabelValue(item.projectID, context?.projectID),
    organizationID: resolveIdToLabelValue(
      item.organizationID,
      context?.organizationID,
    ),
    byUserID: resolveIdToLabelValue(item.byUserID, context?.byUserID),
    status: item.status ?? 'enabled',
    updatedAt: item.updatedAt,
    dimensionRange:
      item.dimensionRange?.map(({ key, min, max }) => ({
        key: key ?? '',
        min,
        max,
      })) ?? [],
    updatedBy: undefined,
  } as AdaptedProduct
}
